import { useState, useEffect, useCallback } from "react";
import { useNotifications } from "../contexts/NotificationContext";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../services/api";
import TokenLogo from "./logo.png";

type Stats = {
	price: number;
	holders: number;
	marketCap: number;
	volume24h: number;
	priceChange24h: number;
};

const StatsDashboard = () => {
	const { addNotification } = useNotifications();
	const [stats, setStats] = useState<Stats | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [showTooltip, setShowTooltip] = useState(true);

	const fetchData = useCallback(async () => {
		try {
			const data = await api.getTokenData();
			setStats((prevStats) => {
				// Notifica mudanças significativas apenas se já existirem dados anteriores
				if (
					prevStats &&
					Math.abs(((data.price - prevStats.price) / prevStats.price) * 100) > 5
				) {
					const isPositive = data.price > prevStats.price;
					addNotification({
						message: `Price ${isPositive ? "Up" : "Down"} ${Math.abs(
							((data.price - prevStats.price) / prevStats.price) * 100
						).toFixed(2)}%!`,
						type: isPositive ? "success" : "warning",
						icon: isPositive ? "🚀" : "📉",
					});
				}
				return data;
			});
			setError(null);
		} catch (err) {
			setError("Token data not available yet");
			console.error("Error fetching token data:", err);
		} finally {
			setLoading(false);
		}
	}, [addNotification]);

	useEffect(() => {
		fetchData();
		const interval = setInterval(fetchData, 30000);
		return () => clearInterval(interval);
	}, [fetchData]);

	return (
		<div className='h-full'>
			<div className='flex items-center justify-between mb-6'>
				<div className='flex items-center gap-2'>
					<h2 className='text-3xl font-black text-trump-yellow'>TOKEN STATS</h2>
					<div className='relative group'>
						<span className='cursor-help text-white/60 hover:text-white'>ℹ️</span>
						<div className='absolute left-0 top-full mt-2 w-64 p-4 bg-black/90 rounded-xl shadow-xl z-10 hidden group-hover:block'>
							<p className='text-white text-sm'>
								Live data integration coming in the next update! 🚀
							</p>
						</div>
					</div>
				</div>
				<div className='relative group'>
					<img
						src='/trump-logo.png'
						alt='Trump Token'
						className='w-12 h-12 animate-pulse'
					/>
					{showTooltip && (
						<motion.div
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: 10 }}
							className='absolute right-0 top-full mt-2 w-64 p-4 bg-black/90 rounded-xl shadow-xl z-10'>
							<p className='text-white text-sm'>
								$TRUMP is the official meme token of the Trump community on
								Abstract Network! 🚀
							</p>
						</motion.div>
					)}
				</div>
			</div>

			{loading ? (
				<div className='grid grid-cols-2 gap-4'>
					{[1, 2, 3, 4].map((i) => (
						<div key={i} className='animate-pulse'>
							<div className='h-24 bg-white/10 rounded-xl' />
						</div>
					))}
				</div>
			) : error ? (
				<div className='text-center py-8'>
					<p className='text-trump-red text-lg'>{error}</p>
				</div>
			) : (
				<div className='grid grid-cols-2 gap-4'>
					{[
						{
							label: "Price",
							value: `$${stats?.price.toFixed(4)}`,
							change: stats?.priceChange24h,
							icon: "💰",
						},
						{
							label: "Market Cap",
							value: `$${stats?.marketCap.toLocaleString()}`,
							change: 0,
							icon: "📊",
						},
						{
							label: "24h Volume",
							value: `$${stats?.volume24h.toLocaleString()}`,
							change: 0,
							icon: "📈",
						},
						{
							label: "Holders",
							value: stats?.holders.toLocaleString() || "0",
							change: 0,
							icon: "👥",
						},
					].map((stat, index) => (
						<motion.div
							key={stat.label}
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: index * 0.1 }}
							className='bg-black/30 p-4 rounded-xl border-2 border-white/10 hover:border-white/30 transition-all duration-300 transform hover:scale-105'>
							<div className='flex items-center gap-3 mb-2'>
								<span className='text-2xl'>{stat.icon}</span>
								<h3 className='text-white/80 font-medium'>{stat.label}</h3>
							</div>
							<div className='flex items-baseline justify-between'>
								<p className='text-xl font-bold text-white'>{stat.value}</p>
								{stat.change !== undefined && (
									<p
										className={`text-sm font-bold ${
											stat.change >= 0 ? "text-green-400" : "text-red-400"
										}`}>
										{stat.change >= 0 ? "+" : ""}
										{stat.change.toFixed(2)}%
									</p>
								)}
							</div>
						</motion.div>
					))}
				</div>
			)}
		</div>
	);
};

export default StatsDashboard;
